/**
 * Phase 20 / Task 20.7 — pure state for §80 step 3 (preview and validation).
 *
 * ── Which findings are errors ──────────────────────────────────────────────
 * A finding carries no severity of its own — the API decides it from the code
 * (`isError` in import-validate/rules.ts plus the resolution codes), and that
 * set is deliberately not copied here, because a copy is a drift waiting to
 * happen. Instead a cell's colour is read from the family's own `errors` and
 * `warnings` lists, which the API built from that same set: a finding whose
 * (row, message) appears under `errors` is an error. One source, no copy.
 */

import type { FamilyPreview, Finding, PreviewRow } from './types';

export type RowFilter = 'ALL' | 'ERROR' | 'WARNING' | 'OK';

export type CellTone = 'ERROR' | 'WARNING' | 'FIXED';

const key = (rowNumber: number, message: string): string =>
  `${rowNumber.toString()}\u0000${message}`;

export interface FindingIndex {
  readonly errors: ReadonlySet<string>;
  readonly warnings: ReadonlySet<string>;
}

export function indexFindings(family: FamilyPreview): FindingIndex {
  return {
    errors: new Set(family.errors.map((e) => key(e.rowNumber, e.message))),
    warnings: new Set(family.warnings.map((w) => key(w.rowNumber, w.message))),
  };
}

export function findingTone(index: FindingIndex, rowNumber: number, f: Finding): CellTone {
  if (f.autoFixed) return 'FIXED';
  return index.errors.has(key(rowNumber, f.message)) ? 'ERROR' : 'WARNING';
}

/** The strongest tone among a cell's findings, or null for a clean cell. */
export function cellTone(
  index: FindingIndex,
  row: PreviewRow,
  field: string,
): { readonly tone: CellTone; readonly messages: readonly string[] } | null {
  const hits = row.findings.filter((f) => f.field === field);
  if (hits.length === 0) return null;
  const tones = hits.map((f) => findingTone(index, row.rowNumber, f));
  const tone: CellTone = tones.includes('ERROR')
    ? 'ERROR'
    : tones.includes('WARNING')
      ? 'WARNING'
      : 'FIXED';
  return { tone, messages: hits.map((f) => f.message) };
}

export function filterRows(rows: readonly PreviewRow[], filter: RowFilter): readonly PreviewRow[] {
  return filter === 'ALL' ? rows : rows.filter((r) => r.severity === filter);
}

/** §80: "Row count shown per filter" — from the complete counts, not the 500-row page. */
export function filterCount(family: FamilyPreview, filter: RowFilter): number {
  switch (filter) {
    case 'ALL':
      return family.counts.total;
    case 'ERROR':
      return family.counts.error;
    case 'WARNING':
      return family.counts.warning;
    case 'OK':
      return family.counts.valid;
  }
}

/**
 * The columns the preview table shows: the template headers that carry a value
 * or a finding in at least one row, in first-seen order.
 */
export function previewColumns(family: FamilyPreview): readonly string[] {
  const seen = new Set<string>();
  for (const row of family.rows) {
    for (const k of Object.keys(row.mapped)) seen.add(k);
    for (const f of row.findings) seen.add(f.field);
  }
  return [...seen];
}

/** Rows that will be written: everything but ERROR rows. */
export function importableRowCount(families: readonly FamilyPreview[]): number {
  return families.reduce((n, f) => n + f.counts.valid + f.counts.warning, 0);
}

export function totalWarnings(families: readonly FamilyPreview[]): number {
  return families.reduce((n, f) => n + f.counts.warning, 0);
}

export function totalErrors(families: readonly FamilyPreview[]): number {
  return families.reduce((n, f) => n + f.counts.error, 0);
}

/** DOM id of a preview row, for the error list's scroll-and-pulse. */
export function rowDomId(entityFamily: string, rowNumber: number): string {
  return `import-preview-${entityFamily}-${rowNumber.toString()}`;
}
